import type { StockLedgerSearchField } from '@/infrastructure/FulfillmentAdminClient';
import {
  INVENTORY_FIND_PARAM,
  INVENTORY_TAB_PARAM,
  STOCK_HISTORY_FIELD_PARAM,
  STOCK_HISTORY_QUERY_PARAM,
} from '@/routes/routes';
import type { InventoryTab } from './inventoryView';

/** A Stock History search as the address carries it: the key and the term typed under it [SSD-7444]. */
export interface StockHistoryAddress {
  field: StockLedgerSearchField;
  term: string;
}

const TABS: InventoryTab[] = ['current', 'history', 'inbound', 'outbound'];
const FIELDS: StockLedgerSearchField[] = ['SKU', 'ORDER_ID', 'TRACKING_NO', 'BARCODE', 'PRODUCT_NAME'];

/** The search the address asks Stock History to run, or null. A key the screen does not offer reads as SKU. */
export const historySearchInAddress = (params: URLSearchParams): StockHistoryAddress | null => {
  const term = (params.get(STOCK_HISTORY_QUERY_PARAM) ?? '').trim();
  if (!term) {
    return null;
  }
  const named = (params.get(STOCK_HISTORY_FIELD_PARAM) ?? '').trim().toUpperCase();
  const field = FIELDS.find((key) => key === named) ?? 'SKU';
  return { field, term };
};

/** The Current Stocks search term the address carries, or '' [SSD-7527]. */
export const findInAddress = (params: URLSearchParams): string => (params.get(INVENTORY_FIND_PARAM) ?? '').trim();

/** The address without its one-time search term, or null when it carries none. */
export const withoutFind = (params: URLSearchParams): URLSearchParams | null => {
  if (!params.has(INVENTORY_FIND_PARAM)) {
    return null;
  }
  const next = new URLSearchParams(params);
  next.delete(INVENTORY_FIND_PARAM);
  return next;
};

/** The sub-tab the address opens on. A search in the address belongs to Stock History, so it opens there. */
export const tabInAddress = (params: URLSearchParams): InventoryTab => {
  const named = params.get(INVENTORY_TAB_PARAM);
  const tab = TABS.find((key) => key === named);
  if (tab) {
    return tab;
  }
  return historySearchInAddress(params) ? 'history' : 'current';
};

/**
 * The address for a tab and, on Stock History, its search. Params this page does not own are kept; Current
 * Stocks is the landing tab, so it writes no tab at all.
 */
export const inventoryAddress = (
  current: URLSearchParams,
  tab: InventoryTab,
  search: StockHistoryAddress | null,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  [INVENTORY_TAB_PARAM, STOCK_HISTORY_FIELD_PARAM, STOCK_HISTORY_QUERY_PARAM].forEach((name) => next.delete(name));
  if (tab !== 'current') {
    next.set(INVENTORY_TAB_PARAM, tab);
  }
  if (tab === 'history' && search) {
    next.set(STOCK_HISTORY_FIELD_PARAM, search.field);
    next.set(STOCK_HISTORY_QUERY_PARAM, search.term);
  }
  return next;
};
